/**
 * Pages secondaires du site (cahier des charges §19) — elles réutilisent
 * UNIQUEMENT les textes validés (content.js) : aucune promesse ni argument
 * nouveau. Chaque page : fil d'Ariane, un seul H1, liens vers la landing.
 */
import React, { useState } from 'react';
import {
  HOW, ALERT, EMERGENCY, MUTUAL_AID, VIDEO, WHY, SITUATIONS, RESPONSIBILITY, COLLECTIVITES, DATA, FAQ,
  COLLECTIVITE_FORM, CONTACT_EMAIL, DPO_EMAIL, APP_URL, PROS,
} from './content';
import { Icon } from './icons';
import { PageShell, Header, Footer } from './Layout';
import { CollectivitesBlock, DataBlock, Responsibility, FaqList } from './Home';
import { LEGAL_PAGES } from '../legal';

/* ── /comment-ca-marche ───────────────────────────────────────────────── */
export function HowPage() {
  return (
    <PageShell route="/comment-ca-marche" kicker="Comment ça marche ?" title={HOW.title} lead={WHY.p1}>
      <ol className="lp-steps">
        {HOW.steps.map((s) => (
          <li key={s.num} className="reveal"><span className="lp-step-num" aria-hidden="true">{s.num}</span><h2>{s.label}</h2><p>{s.text}</p></li>
        ))}
      </ol>
      <p className="lp-note reveal"><Icon name="alert" size={22} /><span>{HOW.note}</span></p>
      <h2 className="lp-h2-page">{SITUATIONS.title}</h2>
      <ul className="lp-cards lp-cards-4">
        {SITUATIONS.cards.map((c) => (
          <li key={c.title} className="lp-card reveal"><span className="lp-card-icon"><Icon name={c.icon} size={26} /></span><h3>{c.title}</h3><p>{c.text}</p></li>
        ))}
      </ul>
      <div className="lp-ctas">
        <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-lg">UTILISER CIVIK</a>
        <a href="/securite-citoyenne" className="lp-btn lp-btn-outline lp-btn-lg">En cas de danger</a>
      </div>
    </PageShell>
  );
}

/* ── /entraide-citoyenne ──────────────────────────────────────────────── */
export function MutualAidPage() {
  return (
    <PageShell route="/entraide-citoyenne" kicker="Entraide citoyenne" title={MUTUAL_AID.title} lead={MUTUAL_AID.p1}>
      <p className="lp-lead">{MUTUAL_AID.p2}</p>
      <ul className="lp-principles">
        {MUTUAL_AID.principles.map((p) => (
          <li key={p.title} className="reveal"><span className="lp-principle-icon"><Icon name={p.icon} size={28} /></span><h2>{p.title}</h2><p>{p.text}</p></li>
        ))}
      </ul>
      <h2 className="lp-h2-page">{WHY.title}</h2>
      <p>{WHY.p1}</p>
      <p>{WHY.p2}</p>
      <p className="lp-highlight"><Icon name="shield" size={22} /><strong>{WHY.highlight}</strong></p>
      <Responsibility />
      <div className="lp-ctas">
        <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-lg">UTILISER CIVIK</a>
        <a href="/comment-ca-marche" className="lp-btn lp-btn-outline lp-btn-lg">Comment ça marche</a>
      </div>
    </PageShell>
  );
}

/* ── /securite-citoyenne ──────────────────────────────────────────────── */
export function SecurityPage() {
  return (
    <PageShell route="/securite-citoyenne" kicker="Sécurité citoyenne" title={ALERT.title} lead={ALERT.text}>
      <ol className="lp-timeline lp-timeline-light">
        {ALERT.steps.map((s, i) => (
          <li key={s} className="reveal"><span className="lp-timeline-num" aria-hidden="true">{i + 1}</span><span>{s}</span></li>
        ))}
      </ol>
      <p className="lp-disclaimer lp-disclaimer-light reveal" role="note"><Icon name="shield" size={26} /><strong>{ALERT.disclaimer}</strong></p>

      <h2 className="lp-h2-page">{EMERGENCY.title}</h2>
      <ul className="lp-numbers">
        {EMERGENCY.numbers.map((n) => (
          <li key={n.number} className={`lp-number lp-number-${n.number} reveal`}><a href={n.tel}><span className="lp-number-value">{n.number}</span><span className="lp-number-label">{n.label}</span></a></li>
        ))}
      </ul>
      <p className="lp-emergency-text">{EMERGENCY.text}</p>

      <h2 className="lp-h2-page">{VIDEO.title}</h2>
      <p>{VIDEO.p1}</p>
      <p>{VIDEO.p2}</p>
      <p>{VIDEO.p3}</p>
      <div className="lp-ctas">
        <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-lg">UTILISER CIVIK</a>
        <a href={VIDEO.ctaHref} className="lp-btn lp-btn-secondary lp-btn-lg">{VIDEO.cta}</a>
      </div>
    </PageShell>
  );
}

/* ── /collectivites (+ formulaire) ────────────────────────────────────── */
function Field({ f, value, onChange, error }) {
  const id = `coll-${f.name}`;
  return (
    <div className={`lp-field${error ? ' has-error' : ''}`}>
      <label htmlFor={id}>{f.label}{f.required ? <span aria-hidden="true"> *</span> : null}</label>
      <input
        id={id}
        name={f.name}
        type={f.type || 'text'}
        inputMode={f.inputMode}
        pattern={f.pattern}
        autoComplete={f.autoComplete}
        required={f.required}
        aria-required={f.required ? 'true' : undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        value={value}
        onChange={(e) => onChange(f.name, e.target.value)}
      />
      {error ? <p className="lp-field-error" id={`${id}-err`}>{error}</p> : null}
    </div>
  );
}

const EMPTY_FORM = Object.fromEntries(COLLECTIVITE_FORM.fields.map((f) => [f.name, '']).concat([['need', ''], ['consent', false], ['website', '']]));

export function CollectiviteForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [state, setState] = useState({ status: 'idle', errors: {}, message: '' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const errors = {};
    COLLECTIVITE_FORM.fields.forEach((f) => { if (f.required && !String(form[f.name]).trim()) errors[f.name] = 'Champ obligatoire'; });
    if (form.postalCode && !/^\d{5}$/.test(form.postalCode.trim())) errors.postalCode = 'Code postal à 5 chiffres';
    if (form.population && !/^\d{1,9}$/.test(String(form.population).replace(/\s/g, ''))) errors.population = 'Nombre entier';
    if (!form.need.trim()) errors.need = 'Champ obligatoire';
    if (!form.consent) errors.consent = 'Cette case est obligatoire';
    if (Object.keys(errors).length) { setState({ status: 'idle', errors, message: '' }); return; }
    setState({ status: 'sending', errors: {}, message: '' });
    try {
      const r = await fetch('/api/collectivites/demandes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          lastName: form.lastName.trim(), firstName: form.firstName.trim(), role: form.role.trim(), organization: form.organization.trim(),
          postalCode: form.postalCode.trim(), city: form.city.trim(), email: form.email.trim(), phone: form.phone.trim(),
          population: Number(String(form.population).replace(/\s/g, '')), need: form.need.trim(), consent: true, website: form.website,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setState({ status: 'error', errors: data.details || {}, message: data.error || `Erreur ${r.status}` });
        return;
      }
      setState({ status: 'done', errors: {}, message: '' });
      setForm(EMPTY_FORM);
      if (typeof window !== 'undefined' && window.civikTrack) window.civikTrack('form_submit', { form: 'collectivites' });
    } catch {
      setState({ status: 'error', errors: {}, message: 'Réseau indisponible' });
    }
  };

  if (state.status === 'done') {
    return (
      <div className="lp-form-success" role="status" aria-live="polite">
        <Icon name="check" size={32} />
        <p><strong>{COLLECTIVITE_FORM.success}</strong></p>
        <p>Un accusé de réception vous a été adressé par e-mail. CIVIK reviendra vers vous à l'adresse indiquée.</p>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={submit} noValidate data-track-form="collectivites" onFocusCapture={() => { if (typeof window !== 'undefined' && window.civikTrack) window.civikTrack('form_start', { form: 'collectivites' }); }}>
      <p className="lp-form-hint" id="coll-form-hint">Tous les champs marqués * sont obligatoires.</p>
      <div className="lp-form-grid">
        {COLLECTIVITE_FORM.fields.map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={set} error={state.errors[f.name]} />)}
      </div>
      <div className={`lp-field${state.errors.need ? ' has-error' : ''}`}>
        <label htmlFor="coll-need">{COLLECTIVITE_FORM.need.label}<span aria-hidden="true"> *</span></label>
        <textarea id="coll-need" name="need" rows="5" required aria-required="true" aria-invalid={state.errors.need ? 'true' : undefined} value={form.need} onChange={(e) => set('need', e.target.value)} />
        {state.errors.need ? <p className="lp-field-error">{state.errors.need}</p> : null}
      </div>
      {/* Piège à robots : champ invisible, doit rester vide. */}
      <div className="lp-hp" aria-hidden="true">
        <label htmlFor="coll-website">Site web</label>
        <input id="coll-website" name="website" type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} />
      </div>
      <div className={`lp-field lp-field-check${state.errors.consent ? ' has-error' : ''}`}>
        <input id="coll-consent" name="consent" type="checkbox" required aria-required="true" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} aria-invalid={state.errors.consent ? 'true' : undefined} />
        <label htmlFor="coll-consent">{COLLECTIVITE_FORM.consent}<span aria-hidden="true"> *</span> <a href="/politique-confidentialite">Lire la politique de confidentialité</a></label>
        {state.errors.consent ? <p className="lp-field-error">{state.errors.consent}</p> : null}
      </div>
      <p className="lp-form-rgpd">Les informations saisies sont utilisées par CIVIK (MMI Industries) pour répondre à votre demande de contact. Vos droits d'accès, de rectification et d'effacement s'exercent auprès de <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>.</p>
      {state.status === 'error' ? (
        <p className="lp-form-error" role="alert">{COLLECTIVITE_FORM.error}<a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. ({state.message})</p>
      ) : null}
      <button type="submit" className="lp-btn lp-btn-inst lp-btn-lg" disabled={state.status === 'sending'}>
        {state.status === 'sending' ? 'Envoi en cours…' : COLLECTIVITE_FORM.cta}
      </button>
    </form>
  );
}

export function CollectivitesPage() {
  return (
    <PageShell route="/collectivites" kicker="Collectivités" title={COLLECTIVITES.title} lead={COLLECTIVITES.subtitle} wide>
      <CollectivitesBlock asSection={false} showCtas={false} />
      <section id="formulaire" className="lp-form-section" aria-labelledby="form-title">
        <h2 id="form-title">Être contacté par CIVIK</h2>
        <p className="lp-lead">Vous représentez une commune, une intercommunalité ou un service public ? Laissez-nous vos coordonnées : CIVIK vous recontacte.</p>
        <CollectiviteForm />
      </section>
      <section className="lp-page-section" aria-labelledby="coll-faq">
        <h2 id="coll-faq">{FAQ.items[7].q}</h2>
        <p>{FAQ.items[7].a}</p>
        <p><a href="/police-municipale">CIVIK et la police municipale</a> · <a href="/faq">Toutes les questions fréquentes</a></p>
      </section>
    </PageShell>
  );
}

/* ── /police-municipale ───────────────────────────────────────────────── */
export function PoliceMunicipalePage() {
  const b = COLLECTIVITES.benefits;
  return (
    <PageShell route="/police-municipale" kicker="Collectivités · Police municipale" title="CIVIK et la police municipale" lead={COLLECTIVITES.text}>
      <ul className="lp-benefits">
        {[b[3], b[1], b[2]].map((x) => (
          <li key={x.title} className="reveal"><span className="lp-benefit-icon"><Icon name={x.icon} size={26} /></span><div><h2>{x.title}</h2><p>{x.text}</p></div></li>
        ))}
      </ul>
      <p className="lp-disclaimer lp-disclaimer-light reveal" role="note"><Icon name="shield" size={26} /><strong>{ALERT.disclaimer}</strong></p>
      <h2 className="lp-h2-page">{FAQ.items[6].q}</h2>
      <p>{FAQ.items[6].a}</p>
      <p>{FAQ.items[6].a2}</p>
      <h2 className="lp-h2-page">{FAQ.items[7].q}</h2>
      <p>{FAQ.items[7].a}</p>
      <div className="lp-ctas">
        <a href="/collectivites#formulaire" className="lp-btn lp-btn-inst lp-btn-lg" data-track="cta_contact" data-track-place="police-municipale">{COLLECTIVITES.ctaSecondary}</a>
        <a href="/collectivites" className="lp-btn lp-btn-outline lp-btn-lg">{COLLECTIVITES.ctaPrimary}</a>
      </div>
    </PageShell>
  );
}

/* ── /confidentialite-donnees ─────────────────────────────────────────── */
export function DataPage() {
  return (
    <PageShell route="/confidentialite-donnees" kicker="Données & confidentialité" title={DATA.title} lead={DATA.p1} wide>
      <p className="lp-lead">{DATA.p2}</p>
      <ul className="lp-cards lp-cards-4 lp-cards-data">
        {DATA.commitments.map((c) => (
          <li key={c.title} className="lp-card reveal"><span className="lp-card-icon"><Icon name={c.icon} size={26} /></span><h2>{c.title}</h2><p>{c.text}</p></li>
        ))}
      </ul>
      <h2 className="lp-h2-page">{VIDEO.title}</h2>
      <p>{VIDEO.p1}</p>
      <p>{VIDEO.p2}</p>
      <p>{VIDEO.p3}</p>
      <h2 className="lp-h2-page">Vos questions sur les données</h2>
      <div className="lp-faq-list">
        {[3, 4, 5, 6].map((i) => (
          <details key={i} className="lp-faq-item"><summary><span className="lp-faq-q">{FAQ.items[i].q}</span><Icon name="chevron" size={22} className="lp-faq-chevron" /></summary><div className="lp-faq-a"><p>{FAQ.items[i].a}</p>{FAQ.items[i].a2 ? <p>{FAQ.items[i].a2}</p> : null}</div></details>
        ))}
      </div>
      <h2 className="lp-h2-page">Documents de référence</h2>
      <ul className="lp-links">
        <li><a href="/politique-confidentialite">Politique de confidentialité</a> — données traitées, finalités, durées, destinataires, droits.</li>
        <li><a href="/politique-cookies">Politique de cookies</a> — le site ne dépose aucun cookie soumis à consentement.</li>
        <li><a href="/mentions-legales">Mentions légales</a> — éditeur, hébergeurs, contact.</li>
        <li>Délégué à la protection des données : <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a>.</li>
      </ul>
    </PageShell>
  );
}

/* ── /faq ─────────────────────────────────────────────────────────────── */
export function FaqPage() {
  return (
    <PageShell route="/faq" kicker="FAQ" title={FAQ.title}>
      <FaqList headingId="faqpage" />
      <div className="lp-ctas">
        <a href={APP_URL} className="lp-btn lp-btn-primary lp-btn-lg">UTILISER CIVIK</a>
        <a href="/contact" className="lp-btn lp-btn-outline lp-btn-lg">Une autre question ? Contact</a>
      </div>
    </PageShell>
  );
}

/* ── /contact ─────────────────────────────────────────────────────────── */
export function ContactPage() {
  return (
    <PageShell route="/contact" kicker="Contact" title="Contacter CIVIK" lead="Une question sur l'application, une demande de collectivité ou de professionnel ? Voici comment nous joindre.">
      <ul className="lp-contact-cards">
        <li className="lp-card reveal">
          <span className="lp-card-icon"><Icon name="mail" size={26} /></span>
          <h2>Citoyens et utilisateurs</h2>
          <p>Questions sur l'application, votre compte ou votre abonnement.</p>
          <p><a className="lp-btn lp-btn-outline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
        </li>
        <li className="lp-card reveal">
          <span className="lp-card-icon"><Icon name="building" size={26} /></span>
          <h2>Collectivités</h2>
          <p>{COLLECTIVITES.subtitle}</p>
          <p><a className="lp-btn lp-btn-inst" href="/collectivites#formulaire">{COLLECTIVITES.ctaSecondary}</a></p>
        </li>
        <li className="lp-card reveal">
          <span className="lp-card-icon"><Icon name="shield" size={26} /></span>
          <h2>Professionnels</h2>
          <p>{PROS.intro}</p>
          <p><a className="lp-btn lp-btn-inst" href={PROS.ctaHref} data-track="cta_candidater" data-track-place="contact">{PROS.cta}</a></p>
        </li>
        <li className="lp-card reveal">
          <span className="lp-card-icon"><Icon name="lock" size={26} /></span>
          <h2>Données personnelles</h2>
          <p>Exercice de vos droits (accès, rectification, effacement, opposition).</p>
          <p><a className="lp-btn lp-btn-outline" href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a></p>
        </li>
      </ul>
      <p className="lp-disclaimer lp-disclaimer-light" role="note"><Icon name="alert" size={26} /><strong>{EMERGENCY.text}</strong></p>
    </PageShell>
  );
}

/* ── /accessibilite ───────────────────────────────────────────────────── */
export function AccessibilityPage() {
  return (
    <PageShell route="/accessibilite" kicker="Accessibilité" title="Accessibilité du site civik.tech" lead="CIVIK souhaite rendre son site accessible à toutes et à tous, conformément à l'article 47 de la loi n° 2005-102 du 11 février 2005.">
      <h2>État de conformité</h2>
      <p>Le site civik.tech n'a pas encore fait l'objet d'un audit de conformité au Référentiel général d'amélioration de l'accessibilité (RGAA). Aucun taux de conformité ne peut donc être affiché à ce jour. La déclaration d'accessibilité complète sera publiée à l'issue de cet audit.</p>
      <h2>Ce qui est mis en œuvre</h2>
      <ul className="lp-links">
        <li>Structure HTML sémantique (un seul titre principal par page, hiérarchie des titres, listes, régions).</li>
        <li>Navigation complète au clavier, lien d'évitement « Aller au contenu », focus visible sur tous les éléments interactifs.</li>
        <li>Contrastes de couleurs conformes au niveau AA des WCAG 2.1 pour les textes et les boutons.</li>
        <li>Textes alternatifs sur les images porteuses de sens ; pictogrammes décoratifs masqués aux lecteurs d'écran.</li>
        <li>Respect de la préférence « réduire les animations » du système (prefers-reduced-motion).</li>
        <li>Aucune information transmise uniquement par la couleur ; tailles de police et zones tactiles adaptées au mobile.</li>
      </ul>
      <h2>Signaler un défaut d'accessibilité</h2>
      <p>Si vous rencontrez une difficulté d'accès à un contenu ou à une fonctionnalité, écrivez-nous à <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> en décrivant le problème et la page concernée. Nous vous répondrons dans les meilleurs délais.</p>
      <h2>Voies de recours</h2>
      <p>Si vous n'obtenez pas de réponse satisfaisante, vous pouvez adresser une réclamation au Défenseur des droits (defenseurdesdroits.fr) ou par courrier gratuit, sans affranchissement : Défenseur des droits, Libre réponse 71120, 75342 Paris CEDEX 07.</p>
    </PageShell>
  );
}

/* ── Pages légales (contenu : src/legal.js, inchangé) ─────────────────── */
export function LegalPage({ slug }) {
  const page = LEGAL_PAGES[slug];
  return (
    <PageShell route={slug} kicker="Juridique" title={page.title} lead={`Dernière mise à jour : ${page.updated}`}>
      <div className="lp-legal">
        {page.sections.map((sec, i) => (
          <section key={i} className="lp-legal-section">
            {sec.heading && <h2>{sec.heading}</h2>}
            {sec.paragraphs.map((para, j) => <p key={j}>{para}</p>)}
            {sec.list && <ul>{sec.list.map((li, k) => <li key={k}>{li}</li>)}</ul>}
          </section>
        ))}
        <p className="lp-legal-contact">Une question ? <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> · Données personnelles : <a href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</a></p>
      </div>
    </PageShell>
  );
}

/* ── 404 ──────────────────────────────────────────────────────────────── */
export function NotFoundPage({ route }) {
  return (
    <>
      <Header route={route} />
      <main id="contenu" className="lp-page">
        <div className="lp-container lp-page-body lp-center lp-404">
          <p className="lp-kicker">Erreur 404</p>
          <h1>Page introuvable</h1>
          <p className="lp-lead">L'adresse demandée n'existe pas ou a été déplacée.</p>
          <div className="lp-ctas lp-ctas-center">
            <a href="/" className="lp-btn lp-btn-primary lp-btn-lg">Retour à l'accueil</a>
            <a href={APP_URL} className="lp-btn lp-btn-outline lp-btn-lg">Utiliser CIVIK</a>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

/* Sections réutilisées dans plusieurs pages */
export { RESPONSIBILITY };
